import { useState, useEffect, useRef } from 'react';
import { Plus, Clock, Trash2, User, X, Calendar, Download, Search, ChevronDown, RotateCcw } from '../../icons/wp-icons';
import { getZeiterfassungen, addZeiterfassung, deleteZeiterfassung, updateZeiterfassung, getZeitStatistiken, getTerminZeiterfassungen, importTerminZeiterfassung } from '../../api/projektApi';
import { getAllBenutzer } from '../../api/benutzerApi';
import { katalogApi } from '../../api/katalogApi';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function ProjektZeiterfassung({ projektId, onDataChange }) {
  const { user } = useAuth();
  const [zeiterfassungen, setZeiterfassungen] = useState([]);
  const [statistiken, setStatistiken] = useState(null);
  const [benutzer, setBenutzer] = useState([]);
  const [katalog, setKatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [terminZeiterfassungen, setTerminZeiterfassungen] = useState([]);
  const [importLoading, setImportLoading] = useState(false);

  // Katalog-Suche State
  const [katalogSuche, setKatalogSuche] = useState('');
  const [showKatalogDropdown, setShowKatalogDropdown] = useState(false);
  const [selectedKatalog, setSelectedKatalog] = useState(null);
  const katalogDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    benutzerId: '',
    datum: new Date().toISOString().split('T')[0],
    startzeit: '08:00',
    endzeit: '16:00',
    stunden: '',
    stundensatz: '',
    taetigkeit: '',
    notiz: ''
  });

  useEffect(() => {
    loadData();
  }, [projektId]);

  useEffect(() => {
    // Set current user as default
    if (user && !formData.benutzerId) {
      setFormData(prev => ({ ...prev, benutzerId: user.id }));
    }
  }, [user]);

  // Schließe Dropdown wenn außerhalb geklickt wird
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (katalogDropdownRef.current && !katalogDropdownRef.current.contains(event.target)) {
        setShowKatalogDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      const [zeitData, statsData, benutzerData, katalogData] = await Promise.all([
        getZeiterfassungen(projektId),
        getZeitStatistiken(projektId),
        getAllBenutzer().catch(() => []),
        katalogApi.getPositionen({ aktiv: true }).catch(() => [])
      ]);
      setZeiterfassungen(zeitData);
      setStatistiken(statsData);
      setBenutzer(benutzerData || []);
      // Nur Katalogpositionen mit Einheit "Std" oder "Stunden"
      const stundenKatalog = (katalogData || []).filter(k =>
        k.einheit?.toLowerCase() === 'std' ||
        k.einheit?.toLowerCase() === 'stunden' ||
        k.einheit?.toLowerCase() === 'std.'
      );
      setKatalog(stundenKatalog);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      // Wenn manuelle Stunden eingegeben, diese verwenden
      if (formData.stunden) {
        data.stunden = parseFloat(formData.stunden);
      }
      // Stundensatz aus Formular (in Euro, Backend erwartet Euro)
      if (formData.stundensatz) {
        data.stundensatz = parseFloat(formData.stundensatz);
      }
      await addZeiterfassung(projektId, data);
      toast.success('Zeiterfassung hinzugefügt');
      setShowAdd(false);
      resetForm();
      loadData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Hinzufügen');
    }
  };

  const resetForm = () => {
    setFormData({
      benutzerId: user?.id || '',
      datum: new Date().toISOString().split('T')[0],
      startzeit: '08:00',
      endzeit: '16:00',
      stunden: '',
      stundensatz: '',
      taetigkeit: '',
      notiz: ''
    });
    setSelectedKatalog(null);
    setKatalogSuche('');
  };

  // Gefilterte Katalog-Liste basierend auf Suche
  const gefilterterKatalog = katalog.filter(kat => {
    const suchText = katalogSuche.toLowerCase();
    return (
      kat.bezeichnung?.toLowerCase().includes(suchText) ||
      kat.artikelnummer?.toLowerCase().includes(suchText) ||
      kat.kategorie?.toLowerCase().includes(suchText)
    );
  });

  // Katalog-Position auswählen
  const handleKatalogSelect = (kat) => {
    setSelectedKatalog(kat);
    setFormData({
      ...formData,
      taetigkeit: kat.bezeichnung,
      stundensatz: kat.verkaufspreis || ''
    });
    setKatalogSuche('');
    setShowKatalogDropdown(false);
  };

  // Katalog-Auswahl entfernen
  const handleKatalogRemove = () => {
    setSelectedKatalog(null);
    setFormData({
      ...formData,
      taetigkeit: '',
      stundensatz: ''
    });
    setKatalogSuche('');
  };

  const handleDelete = async (zeiterfassungId) => {
    if (!confirm('Zeiterfassung wirklich löschen?')) return;
    try {
      await deleteZeiterfassung(projektId, zeiterfassungId);
      toast.success('Zeiterfassung gelöscht');
      loadData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const handleToggleAbgerechnet = async (zeiterfassungId, currentStatus) => {
    const newStatus = !currentStatus;
    const action = newStatus ? 'Als abgerechnet markieren' : 'Abrechnung zurücksetzen';
    if (!confirm(`${action}?`)) return;
    try {
      await updateZeiterfassung(projektId, zeiterfassungId, { abgerechnet: newStatus });
      toast.success(newStatus ? 'Als abgerechnet markiert' : 'Abrechnung zurückgesetzt');
      loadData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Aktualisieren');
    }
  };

  const handleOpenImport = async () => {
    setImportLoading(true);
    setShowImport(true);
    try {
      const data = await getTerminZeiterfassungen(projektId);
      setTerminZeiterfassungen(data);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Laden der Termin-Zeiterfassungen');
      setTerminZeiterfassungen([]);
    } finally {
      setImportLoading(false);
    }
  };

  const handleImport = async (zeiterfassungId) => {
    try {
      await importTerminZeiterfassung(projektId, {
        zeiterfassungId,
        benutzerId: user?.id
      });
      toast.success('Zeiterfassung importiert');
      // Aus Liste entfernen
      setTerminZeiterfassungen(prev => prev.filter(z => z.id !== zeiterfassungId));
      loadData();
      if (onDataChange) onDataChange();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler beim Importieren');
    }
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
  };

  // Berechne Stunden aus Start/Ende
  const calculateHours = () => {
    if (formData.startzeit && formData.endzeit) {
      const [startH, startM] = formData.startzeit.split(':').map(Number);
      const [endH, endM] = formData.endzeit.split(':').map(Number);
      let hours = (endH + endM/60) - (startH + startM/60);
      if (hours < 0) hours += 24;
      return hours.toFixed(2);
    }
    return '';
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="font-semibold text-text flex items-center gap-2">
            <Clock className="w-5 h-5 text-text-muted" />
            Zeiterfassung
          </h3>
          {statistiken && (
            <p className="text-sm text-text-muted mt-1">
              {statistiken.gesamtStunden?.toFixed(1)} Std. = {formatCurrency(statistiken.gesamtNetto)}
              {statistiken.offen > 0 && (
                <span className="text-orange-500 ml-2">({formatCurrency(statistiken.offen)} offen)</span>
              )}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleOpenImport}
            className="flex items-center gap-2 px-3 py-2 border border-border text-text rounded-button hover:bg-surface-elevated transition text-sm"
          >
            <Download className="w-4 h-4" />
            Aus Termin
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition text-sm"
          >
            <Plus className="w-4 h-4" />
            Zeit erfassen
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="bg-surface-elevated rounded-card p-4 mb-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-medium text-text">Neue Zeiterfassung</h4>
            <button onClick={() => setShowAdd(false)} className="p-1 hover:bg-surface rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Katalog-Dropdown für Stundenposition */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1 text-text">Leistung aus Katalog</label>
                <div className="relative" ref={katalogDropdownRef}>
                  {selectedKatalog ? (
                    <div className="flex items-center justify-between w-full px-3 py-2 border border-border rounded-button bg-gray-50">
                      <div className="min-w-0 flex-1">
                        <span className="font-medium text-sm block truncate">{selectedKatalog.bezeichnung}</span>
                        <span className="text-text-muted text-xs block truncate">
                          {formatCurrency(Math.round((selectedKatalog.verkaufspreis || 0) * 100))}/Std.
                          {selectedKatalog.artikelnummer && ` | ${selectedKatalog.artikelnummer}`}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={handleKatalogRemove}
                        className="p-1.5 hover:bg-gray-200 rounded transition ml-2 shrink-0"
                        title="Auswahl entfernen"
                      >
                        <X className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={katalogSuche}
                        onChange={(e) => {
                          setKatalogSuche(e.target.value);
                          setShowKatalogDropdown(true);
                        }}
                        onFocus={() => setShowKatalogDropdown(true)}
                        placeholder="Stunden-Leistung aus Katalog wählen..."
                        className="w-full pl-10 pr-10 py-2 border border-border rounded-button focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-text"
                      />
                      <button
                        type="button"
                        onClick={() => setShowKatalogDropdown(!showKatalogDropdown)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2"
                      >
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showKatalogDropdown ? 'rotate-180' : ''}`} />
                      </button>
                    </div>
                  )}

                  {showKatalogDropdown && !selectedKatalog && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-border rounded-button shadow-lg max-h-60 overflow-y-auto">
                      {katalog.length === 0 ? (
                        <div className="px-4 py-3 text-text-muted text-center">
                          Keine Stunden-Positionen im Katalog (Einheit: Std)
                        </div>
                      ) : gefilterterKatalog.length === 0 ? (
                        <div className="px-4 py-3 text-text-muted text-center">
                          Keine Treffer gefunden
                        </div>
                      ) : (
                        gefilterterKatalog.slice(0, 20).map(kat => (
                          <button
                            key={kat.id}
                            type="button"
                            onClick={() => handleKatalogSelect(kat)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-border last:border-b-0 transition"
                          >
                            <div className="font-medium text-text">{kat.bezeichnung}</div>
                            <div className="text-sm text-text-muted flex gap-3">
                              <span>{formatCurrency(Math.round((kat.verkaufspreis || 0) * 100))}/Std.</span>
                              {kat.artikelnummer && <span>Art.-Nr.: {kat.artikelnummer}</span>}
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-text-muted mt-1">
                  Wählen Sie eine Leistung aus dem Katalog oder geben Sie unten manuell ein
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-text">Mitarbeiter</label>
                <select
                  value={formData.benutzerId}
                  onChange={(e) => setFormData({ ...formData, benutzerId: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                >
                  {benutzer.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Datum</label>
                <input
                  type="date"
                  value={formData.datum}
                  onChange={(e) => setFormData({ ...formData, datum: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Von</label>
                <input
                  type="time"
                  value={formData.startzeit}
                  onChange={(e) => setFormData({ ...formData, startzeit: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Bis</label>
                <input
                  type="time"
                  value={formData.endzeit}
                  onChange={(e) => setFormData({ ...formData, endzeit: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">
                  Oder: Stunden direkt eingeben
                </label>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  value={formData.stunden}
                  onChange={(e) => setFormData({ ...formData, stunden: e.target.value })}
                  placeholder={calculateHours() ? `Berechnet: ${calculateHours()} Std.` : 'z.B. 8.5'}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Stundensatz (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.stundensatz}
                  onChange={(e) => setFormData({ ...formData, stundensatz: e.target.value })}
                  placeholder="z.B. 65.00"
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                />
                <p className="text-xs text-text-muted mt-1">
                  {formData.stundensatz ? formatCurrency(Math.round(parseFloat(formData.stundensatz) * 100)) + '/Std.' : 'Wird aus Mitarbeiterdaten übernommen'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-text">Tätigkeit</label>
                <input
                  type="text"
                  value={formData.taetigkeit}
                  onChange={(e) => setFormData({ ...formData, taetigkeit: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                  placeholder="z.B. Montage, Vorbereitung..."
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1 text-text">Notiz</label>
                <textarea
                  value={formData.notiz}
                  onChange={(e) => setFormData({ ...formData, notiz: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-button bg-surface text-text"
                  rows={2}
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition">
                Hinzufügen
              </button>
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 border border-border rounded-button hover:bg-surface-elevated transition">
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {zeiterfassungen.length === 0 ? (
        <p className="text-text-muted text-center py-8">Keine Zeiterfassungen</p>
      ) : (
        <div className="space-y-2">
          {zeiterfassungen.map(z => (
            <div
              key={z.id}
              className={`flex items-center justify-between p-4 rounded-button ${z.abgerechnet ? 'bg-green-50 border border-green-200' : 'bg-surface-elevated'}`}
            >
              <div className="flex items-center gap-4">
                <Clock className="w-5 h-5 text-text-muted" />
                <div>
                  <p className="font-medium text-text">{z.taetigkeit || 'Projektarbeit'}</p>
                  <p className="text-sm text-text-muted">
                    {new Date(z.datum).toLocaleDateString('de-DE')}
                    {z.startzeit && z.endzeit && ` | ${z.startzeit} - ${z.endzeit}`}
                    {' | '}{z.stunden?.toFixed(1)} Std. x {formatCurrency(Math.round(z.stundensatz * 100))}/Std.
                  </p>
                  <p className="text-xs text-text-muted flex items-center gap-1 mt-1">
                    <User className="w-3 h-3" /> {z.benutzer?.name}
                    {z.notiz && <span className="ml-2">| {z.notiz}</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-right">
                  <span className="font-semibold text-text">{formatCurrency(z.gesamtNetto)}</span>
                  {z.abgerechnet && (
                    <span className="block text-xs text-green-600 font-medium">Abgerechnet</span>
                  )}
                </div>
                {z.abgerechnet ? (
                  <button
                    onClick={() => handleToggleAbgerechnet(z.id, z.abgerechnet)}
                    className="p-2 text-orange-500 hover:bg-orange-50 rounded transition"
                    title="Abrechnung zurücksetzen"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => handleDelete(z.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded transition"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Statistik pro Mitarbeiter */}
      {statistiken?.proMitarbeiter?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <h4 className="text-sm font-medium text-text-muted mb-3">Aufwand pro Mitarbeiter</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {statistiken.proMitarbeiter.map(pm => (
              <div key={pm.benutzer?.id} className="bg-surface-elevated rounded-button p-3">
                <p className="font-medium text-text text-sm">{pm.benutzer?.name}</p>
                <p className="text-text-muted text-xs">
                  {pm.stunden?.toFixed(1)} Std. = {formatCurrency(pm.netto)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-card p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-text flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Zeiterfassungen aus Terminen importieren
              </h3>
              <button onClick={() => setShowImport(false)} className="p-2 hover:bg-surface-elevated rounded-button">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-text-muted mb-4">
              Wählen Sie Zeiterfassungen aus Terminen des Kunden, die Sie ins Projekt übernehmen möchten.
            </p>

            <div className="flex-1 overflow-y-auto">
              {importLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : terminZeiterfassungen.length === 0 ? (
                <p className="text-text-muted text-center py-8">
                  Keine verfügbaren Zeiterfassungen aus Terminen gefunden
                </p>
              ) : (
                <div className="space-y-2">
                  {terminZeiterfassungen.map(z => (
                    <div
                      key={z.id}
                      className="flex items-center justify-between p-4 bg-surface-elevated rounded-button"
                    >
                      <div className="flex items-center gap-4">
                        <Calendar className="w-5 h-5 text-text-muted" />
                        <div>
                          <p className="font-medium text-text">{z.taetigkeit}</p>
                          <p className="text-sm text-text-muted">
                            {z.datum ? new Date(z.datum).toLocaleDateString('de-DE') : '-'}
                            {' | '}{z.stunden?.toFixed(1)} Std. x {formatCurrency(Math.round(z.stundensatz * 100))}/Std.
                          </p>
                          {z.termin && (
                            <p className="text-xs text-text-muted mt-1">
                              Termin: {z.termin.titel || new Date(z.termin.datum).toLocaleDateString('de-DE')} um {z.termin.uhrzeit}
                            </p>
                          )}
                          {z.inRechnungId && (
                            <p className="text-xs text-orange-500 mt-1">
                              Bereits in Rechnung
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-text">{formatCurrency(z.gesamtNetto)}</span>
                        <button
                          onClick={() => handleImport(z.id)}
                          className="px-3 py-1.5 bg-primary text-white rounded-button hover:bg-primary-dark transition text-sm"
                        >
                          Importieren
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-border">
              <button
                onClick={() => setShowImport(false)}
                className="w-full px-4 py-2 border border-border rounded-button hover:bg-surface-elevated transition"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
